import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { presenceState, type Participant } from '@databases/presence';
import { defaultStoreUtils } from '@databases/redux';
import { applyDatabaseDiff } from '@databases/utils/redux/databases/actions';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { getBackendAgentEditedFieldIds } from '../backend-agent-field-edits.ts';
import { getRenderedBackendAgentFieldIds } from '../backend-agent-field-targets.ts';
import { getBackendAgentStreamingParticipant } from '../backend-agent-streaming-participant.ts';

export const BACKEND_AGENT_FIELD_EDIT_PRESENCE_DELAY_MS = 700;

const MAX_REVEAL_TICKS = 4;
const DEFERRED_FIELD_PROJECTION_META_KEY = Symbol.for(
  'databases:deferred-field-projection'
);

type DatabaseTransaction = Parameters<CollabManager['database']['getDiff']>[0];

interface UseBackendAgentFieldEditPresenceOptions {
  collabManager: CollabManager | undefined;
  contentId: string;
  isPaused: boolean;
  layoutRoot: HTMLElement | null;
  onEligibleBackendAgentFieldEdit?: () => void;
  resetKey: string;
}

export type ProgressiveFieldEditRevealResult =
  | { kind: 'idle' }
  | { activeFieldId: string; kind: 'revealing'; participant: Participant };

interface QueuedFieldEdit {
  fieldId: string;
  participant: Participant;
}

export function useBackendAgentFieldEditPresence({
  collabManager,
  contentId,
  isPaused,
  layoutRoot,
  onEligibleBackendAgentFieldEdit,
  resetKey
}: UseBackendAgentFieldEditPresenceOptions): ProgressiveFieldEditRevealResult {
  const isDatabaseBEStreamingEnabled = getExperimentValue(
    'databaseBEStreaming',
    'enabled',
    { fireExperimentExposure: false }
  );
  const dispatch = defaultStoreUtils.useDispatch();
  const previousFieldIds = useRef<readonly string[]>([]);
  const revealBatchSize = useRef(1);
  const isInitialRevealBatch = useRef(true);
  const queuedFieldIdsRef = useRef<readonly QueuedFieldEdit[]>([]);
  const [queuedFieldIds, setQueuedFieldIds] = useState<readonly QueuedFieldEdit[]>([]);

  const updateQueuedFieldIds = useCallback(
    (
      update: (queuedFieldIds: readonly QueuedFieldEdit[]) => readonly QueuedFieldEdit[]
    ) => {
      setQueuedFieldIds((currentQueuedFieldIds) => {
        const nextQueuedFieldIds = update(currentQueuedFieldIds);
        if (nextQueuedFieldIds.length === 0) {
          isInitialRevealBatch.current = true;
          revealBatchSize.current = 1;
        }
        queuedFieldIdsRef.current = nextQueuedFieldIds;
        return nextQueuedFieldIds;
      });
    },
    []
  );

  const projectFieldIds = useCallback(
    (fieldIds: readonly string[]) => {
      if (!collabManager || fieldIds.length === 0) {
        return;
      }

      dispatch(
        applyDatabaseDiff({
          contentId,
          diff: {
            fieldsOrderingChanged: false,
            entriesOrderingChanged: false,
            changedFieldIds: [...fieldIds],
            changedEntryIds: [],
            threadsChanged: false,
            viewsChanged: false,
            defaultEntryChanged: false,
            othersChanged: false
          },
          collabDatabase: collabManager.database,
          isReadOnly: collabManager.isReadOnly
        })
      );
    },
    [collabManager, contentId, dispatch]
  );

  useLayoutEffect(() => {
    queuedFieldIdsRef.current = [];
    isInitialRevealBatch.current = true;
    revealBatchSize.current = 1;
    setQueuedFieldIds([]);

    if (!collabManager) {
      previousFieldIds.current = [];
      return;
    }

    previousFieldIds.current = collabManager.database.fields;
    const onAfterTransaction = (transaction: DatabaseTransaction) => {
      const diff = collabManager.database.getDiff(transaction);
      const currentFieldIds = collabManager.database.fields;
      const editedFieldIds = getBackendAgentEditedFieldIds({
        changedFieldIds: diff.changedFieldIds,
        currentFieldIds,
        previousFieldIds: previousFieldIds.current,
        transaction
      });
      previousFieldIds.current = currentFieldIds;
      const editedFieldIdSet = new Set(editedFieldIds);

      if (queuedFieldIdsRef.current.length > 0 && diff.changedFieldIds.length > 0) {
        const changedFieldIdSet = new Set(diff.changedFieldIds);
        updateQueuedFieldIds((currentQueuedFieldIds) =>
          currentQueuedFieldIds.filter(
            ({ fieldId }) =>
              !changedFieldIdSet.has(fieldId) || editedFieldIdSet.has(fieldId)
          )
        );
      }

      if (editedFieldIds.length === 0 || !layoutRoot) {
        return;
      }

      const renderedFieldIds = getRenderedBackendAgentFieldIds(
        layoutRoot,
        editedFieldIds
      );
      const visibleEditedFieldIds = currentFieldIds.filter(
        (fieldId) => renderedFieldIds.has(fieldId) && editedFieldIdSet.has(fieldId)
      );
      if (visibleEditedFieldIds.length === 0) {
        return;
      }

      onEligibleBackendAgentFieldEdit?.();
      if (!isDatabaseBEStreamingEnabled) {
        return;
      }

      transaction.meta.set(DEFERRED_FIELD_PROJECTION_META_KEY, {
        fieldIds: visibleEditedFieldIds,
        deferFieldsOrdering: false
      });
      updateQueuedFieldIds((currentQueuedFieldIds) => {
        if (currentQueuedFieldIds.length === 0) {
          isInitialRevealBatch.current = true;
        }

        const visibleEditedFieldIdSet = new Set(visibleEditedFieldIds);
        const participant = getBackendAgentStreamingParticipant(
          transaction,
          collabManager
        );
        const nextQueuedFieldIds = [
          ...currentQueuedFieldIds.filter(
            ({ fieldId }) => !visibleEditedFieldIdSet.has(fieldId)
          ),
          ...visibleEditedFieldIds.map((fieldId) => ({ fieldId, participant }))
        ];
        const queuedFieldCount =
          nextQueuedFieldIds.length - (isInitialRevealBatch.current ? 1 : 0);
        revealBatchSize.current = Math.max(
          revealBatchSize.current,
          Math.ceil(queuedFieldCount / MAX_REVEAL_TICKS)
        );
        return nextQueuedFieldIds;
      });
    };

    collabManager.database.doc.on('afterTransaction', onAfterTransaction);
    return () => {
      collabManager.database.doc.off('afterTransaction', onAfterTransaction);
      projectFieldIds(queuedFieldIdsRef.current.map(({ fieldId }) => fieldId));
      queuedFieldIdsRef.current = [];
    };
  }, [
    collabManager,
    isDatabaseBEStreamingEnabled,
    layoutRoot,
    onEligibleBackendAgentFieldEdit,
    projectFieldIds,
    resetKey,
    updateQueuedFieldIds
  ]);

  const activeBatchSize = isInitialRevealBatch.current
    ? Math.min(1, queuedFieldIds.length)
    : Math.min(revealBatchSize.current, queuedFieldIds.length);
  const activeField = queuedFieldIds[activeBatchSize - 1];
  const activeFieldId = activeField?.fieldId;

  useLayoutEffect(() => {
    if (!activeFieldId || isPaused) {
      return;
    }

    projectFieldIds(
      queuedFieldIds.slice(0, activeBatchSize).map(({ fieldId }) => fieldId)
    );
  }, [activeBatchSize, activeFieldId, isPaused, projectFieldIds, queuedFieldIds]);

  useEffect(() => {
    if (!activeField || isPaused) {
      return;
    }

    presenceState.addParticipant(
      { fieldId: activeField.fieldId },
      activeField.participant
    );
    const timeout = setTimeout(() => {
      isInitialRevealBatch.current = false;
      updateQueuedFieldIds((currentQueuedFieldIds) =>
        currentQueuedFieldIds.slice(activeBatchSize)
      );
    }, BACKEND_AGENT_FIELD_EDIT_PRESENCE_DELAY_MS);
    return () => {
      clearTimeout(timeout);
      presenceState.removeParticipant(activeField.participant);
    };
  }, [activeBatchSize, activeField, isPaused, updateQueuedFieldIds]);

  return isDatabaseBEStreamingEnabled && activeField && !isPaused
    ? {
        activeFieldId: activeField.fieldId,
        kind: 'revealing',
        participant: activeField.participant
      }
    : { kind: 'idle' };
}
